import Star20 from '@carbon/icons-react/es/star/20';
import { useBoolean } from 'ahooks';
import { Popover, Space, Table as AntTable } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { TaskId } from 'api/models';
import { Task } from 'api/tasks';
import { Button } from 'components/atoms/button';
import { IconShell } from 'components/atoms/icon-shell';
import { LinkToTask as LinkToTaskAtom } from 'components/atoms/link-to-task';
import { ClassNamePropsWithChildren } from 'components/ozone-component';
import { BodyShort01, Text01 } from 'components/styles';
import { useQueryParams } from 'hooks/use-query-params';
import { CsatForm } from './form';
import { Hierarchy as HierarchyAtom } from './hierarchy';

interface CsatListProps extends ClassNamePropsWithChildren {
  csatPanelOpened?: boolean;
  data?: Task[];
  onSubmit: () => void;
}

const Hierarchy = styled(HierarchyAtom)`
  text-align: left;
  padding-left: 40px;
`;

const Table = styled(AntTable)`
  && .ant-table-tbody > tr > td {
    padding: 0 var(--spacing-03) 0 0;
    border: none;
  }
`;

const LinkToTask = styled(LinkToTaskAtom)`
  & {
    ${BodyShort01};
    ${Text01};
    width: 100%;
    display: block;
    padding-top: var(--spacing-03);
    padding-bottom: var(--spacing-03);
  }
  &:hover {
    color: var(--primary-color);
  }
`;

const RatingCell = ({ csatPanelOpened, task, onSubmit }) => {
  const queryParams = useQueryParams();
  const showCsatForm = queryParams.get('showCsatForm');
  const [visible, { setTrue, setFalse }] = useBoolean(
    Number(showCsatForm) === task.taskId,
  );

  const handleOnOk = () => {
    onSubmit();
    setFalse();
  };

  return (
    <Popover
      key={task.taskId}
      placement="bottom"
      visible={csatPanelOpened && visible}
      trigger="click"
      content={
        <CsatForm
          taskId={task.taskId}
          taskName={task.taskName}
          projectName={task.project.projectName}
          projectPhaseName={task.projectPhase?.projectPhaseName}
          onCancel={setFalse}
          onOk={handleOnOk}
        />
      }
    >
      <Button
        type="link"
        onClick={(e) => {
          e.stopPropagation();
          setTrue();
        }}
      >
        <FormattedMessage id="rate_your_experience" />
      </Button>
    </Popover>
  );
};

export const CsatList: FC<CsatListProps> = (props) => {
  const { csatPanelOpened, data, className, onSubmit } = props;

  const columns = [
    {
      dataIndex: 'taskName',
      key: 'taskName',
      render: (value: string, task: Task) => (
        <LinkToTask id={TaskId(task.project.projectId, task.taskId)}>
          <Space size={0} direction="vertical">
            <Space size="small">
              <IconShell icon={<Star20 />} size="small" />
              <FormattedMessage
                id="milestone_completed"
                values={{ taskName: value }}
              />
            </Space>
            <Hierarchy
              projectName={task.project.projectName}
              projectPhaseName={task.projectPhase?.projectPhaseName}
            />
          </Space>
        </LinkToTask>
      ),
    },
    {
      key: 'rate',
      render: (_, record: Task) => (
        <RatingCell
          csatPanelOpened={csatPanelOpened}
          task={record}
          onSubmit={onSubmit}
        />
      ),
    },
  ];

  return (
    <Table<any>
      size="small"
      className={className}
      dataSource={data}
      columns={columns}
      pagination={false}
      showHeader={false}
    />
  );
};
